import { find, some, cloneDeep, isEqual, isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useHomeUsePluginContainer } from "./container";
import { WEB_APP_DEFAULT_SUBTITLE, WEB_APP_DEFAULT_TITLE, NEW_EDGE_REG } from "@views/thinking-layout-editor/constants";
import useFeatureTags from "@custom-hooks/useFeatureTags";
import { useStore, useDispatch, useSelector } from "react-redux";
import { HomeUsePluginUtil } from "@uikit/util/HomeUsePluginUtil";
import { PreviewAppUtil } from "@uiview/views/PreviewApp/PreviewAppUtil";
import func from "@uikit/func";
import { createContainer } from "unstated-next";
import { useSignal } from "@preact/signals-react";
import { PreviewAppTypeMap } from "@uiview/views/PreviewApp/const";
import { studioActions } from "@store/studio";
import { useModal } from "@uiview/views/AModal/useModal";
import { useIsStaticApp } from "@custom-hooks/useNodePreview";
import { PreviewPaneStore } from "@uiview/views/PreviewPane/PreviewPaneStore";
import { getIt } from "@uikit/getIt";
import isBlank from "@sedan-utils/is-blank";
import useStateRef from "react-usestateref";

/**
 * 为 saas 预览提供状态和内部实现逻辑
 *
 * 在这之前的版本中， saas 预览都使用的是同一份数据(saasUIData)
 * 在当前版本中，saas 预览可以有多份不同预览数据，在这之前是无法做的
 *
 * 但考虑到前期在使用 saasUIData 时处理逻辑和使用方式过多，以下为解决方法：
 * 1. 增加一个额外的预览数据列表(previewAppListSignal)和 previewAppActiveIdSignal 分别表示预览列表和当前预览项目ID
 * 2. 当切换 previewAppActiveIdSignal 时将指定 previewAppList 中的对应项设置给 saasUIData，从使用角度快速处理多个 preview app 的情况
 *
 *
 * **该文件应该属于一个独立的模块,不应在当前目录,且内部状态应该使用 BaseStore 实现,这些都是第一次在创建时没考虑到的问题**
 * @see https://brain-ai.atlassian.net/browse/STUD-1639
 * @see https://brain-ai.atlassian.net/browse/STUD-1722
 * @returns
 */
function usePreviewApp() {
  const featureTags = useFeatureTags();
  const {
    saasAndThinkState,
    aiSaasState,
    homeReactFlowState,
    isNewEdgeRunRef,
    nodesState: { nodesRef, getNodes },
  } = useHomeUsePluginContainer();
  const { setInputId } = aiSaasState;
  const { setSelectedUI, selectedUI } = homeReactFlowState;

  const {
    saasUIDataRef,
    sendToAppStagingData,
    setSaasUIData,
    UIConfigRef,
    saasUIData,
    setCheckedUIList,
    setDisplayThought,
  } = saasAndThinkState;

  const previewAppListSignal = useSignal<PreviewApp.UISaasValueType[]>([]);
  const currentProjectSavedPreviewAppListSignal = useSignal<PreviewApp.UISaasValueType[]>([]);
  const previewAppActiveIdSignal = useSignal<PreviewApp.UISaasValueType["id"]>("");
  const chargeOptionUpdatedSignal = useSignal<string>("");
  const vDataLastIndex = useSignal<number>(0);

  // preview panel 显示状态, null 未挂载, false 不显示,true 显示
  const [showPreviewPanel, _setShowPreviewPanel, showPreviewPanelRef] = useStateRef(false);
  const [historyShowStatus, setHistoryShowStatus] = useState<boolean>(true);
  const [publishShowStatus, setPublishShowStatus] = useState<null | boolean>(null);
  const [previewShowStatus, setPreviewShowStatus] = useState<null | boolean>(null);
  const [settingShowStatus, setSettingShowStatus] = useState<null | boolean>(null);

  const isInHistory = useMemo(() => {
    if (featureTags.enablePublishV3) {
      return historyShowStatus && !publishShowStatus && !previewShowStatus;
    }
    return historyShowStatus && !publishShowStatus && !previewShowStatus && !settingShowStatus;
  }, [historyShowStatus, publishShowStatus, previewShowStatus, settingShowStatus]);

  const showDeleteTipSignal = useSignal(false);

  const store = useStore();
  const showModal = useModal();
  const dispatch = useDispatch();
  const setSelectedTemplate = (val: PreviewApp.ProjectType) => {
    dispatch(studioActions.setSelectedTemplate(val));
  };
  const { isStaticApp } = useIsStaticApp();
  const previewPaneStore = getIt(PreviewPaneStore);
  const thoughts = useSelector((state: any) => state.feed.thoughts) as AISaasOutput.ThoughtType[];
  const { disableSelectTemplate, enable_saas_web, enableStaticMode } = useFeatureTags();

  const previewAppList = previewAppListSignal.value;
  const previewAppAcivieId = previewAppActiveIdSignal.value;

  const appStates = useMemo(() => {
    const chatApp = PreviewAppUtil.isChatAiApp(saasUIDataRef.current);
    return {
      isChatApp: PreviewAppUtil.isChatAiApp(saasUIDataRef.current),
      chatMode: chatApp
        ? PreviewAppUtil.getChatAiMode({
            saasUIData: saasUIDataRef.current,
            nodes: nodesRef.current,
          })
        : undefined,
    };
  }, [previewAppActiveIdSignal.value, nodesRef.current, saasUIData]);

  const openPreviewPage = () => {
    _setShowPreviewPanel(true);
    setPreviewShowStatus(true);
    setHistoryShowStatus(true);
    setPublishShowStatus(null);
    setSettingShowStatus(null);
  };

  const returnToPreviewList = () => {
    setHistoryShowStatus(true);
    setPublishShowStatus(false);
    setPreviewShowStatus(false);
    setSettingShowStatus(false);
    saasUiDataToPreview();
    clearCurrentPreviewApp();

    previewPaneStore.settingPageStatus(false);

    // 如果只有一个publish view，删除后应该直接带用户到choose a format页面
    if (previewAppListSignal.value.length === 0) {
      setHistoryShowStatus(true);
      setPublishShowStatus(true);
    }
  };

  const replacePreviewAppList = (newPreviewAppList: PreviewApp.UISaasValueType[]) => {
    previewAppListSignal.value = newPreviewAppList;
  };

  const checkIfCurrentProjectPreviewListIsChanged = useCallback(() => {
    if (!isEqual(previewAppListSignal.value, currentProjectSavedPreviewAppListSignal.value)) {
      // previewApplistSignal.value只有在回到历史列表时才会更新preview中的数据，
      // 否则只会实时更新convas中的数据，
      // 所以第一步这里判断这里返回true有效；
      // 但相等并不一定代表没有设置preview的更新，比如settings数据有改动，且未返回previewList历史列表页面，那么这里的判断不准确
      // 总结：返回true有效，false得通过后面的判断才准确
      return true;
    }

    // 已保存过的当前打开的previewApp数据，数据不是实时的
    const savedCurrentApp = currentProjectSavedPreviewAppListSignal.value.find(
      app => app.id === previewAppActiveIdSignal.value
    );
    // 实时的previewApp数据
    const currentApp = getSaasUIData();
    // 这里为了修复currentApp一直有默认值导致首页点击返回首页按钮时也会弹出确认框的bug,
    // 取巧修改：针对默认值中没有settings(进入或新建项目后都会设置默认settings)数据来判断是否需要进行有效判断
    // 即走到当前这个方法但没有settings数据时认为是数据出现了问题，直接返回false从而不响应错误数据，后续重构后可删除此判断
    if (isEmpty(currentApp.settings)) {
      return false;
    }
    // 由于当前preview中的逻辑比较复杂，有些数据会发生变化，这里的currentApp数据会有少量不准确的值，目前需求只比较settings和theme
    const savedSettings = {
      ...savedCurrentApp?.settings,
      theme: savedCurrentApp?.theme,
    };
    const currentSettings = {
      ...currentApp.settings,
      theme: currentApp.theme,
    };

    return !isEqual(savedSettings, currentSettings);
  }, [
    previewAppListSignal.value,
    currentProjectSavedPreviewAppListSignal.value,
    previewAppActiveIdSignal.value,
    saasUIDataRef.current,
  ]);

  const updateCurrentProjectSavedPreviewList = useCallback(() => {
    currentProjectSavedPreviewAppListSignal.value = previewAppListSignal.value;
  }, [previewAppListSignal.value]);

  const clearCurrentProjectSavedPreviewList = () => {
    currentProjectSavedPreviewAppListSignal.value = [];
  };

  const addPreview = useCallback((args: PreviewApp.AddPrviewArgs) => {
    const editorStore = (store.getState() as any).editor;
    const selectTemp = getSelectedTemplate(true);
    if (!selectTemp) {
      return;
    }
    // FIXME: 注意 原来的App被删除后，再次添加会重复。需要保存一次过后，就没有这个问题。
    vDataLastIndex.value = (selectTemp?.lastIndex || previewAppListSignal.value.length) + 1;
    const newTarget = PreviewAppUtil.genPreviewApp(getSaasUIData(), {
      previewV2: true,
      theme: editorStore.applicationTheme,
      fontSize: editorStore.fontSize,
      // TODO: 老版本使用 redux isStaticMode, v2 使用 type 区分
      staticMode: args.format.appType === PreviewAppTypeMap.STATIC_WEB,
      ...args.uiData,
      v2App: PreviewAppUtil.genV2App({
        format: args.format,
        lastIndex: vDataLastIndex.value,
        lastTypeIndex: PreviewAppUtil.getAppTypeCountByFormat(previewAppListSignal.value, args.format),
      }),
    });

    previewAppListSignal.value = [...previewAppListSignal.value, newTarget];

    previewToSaasUiData(newTarget.id);
    // syncPreviewAppData(newTarget.id)
    return newTarget;
  }, []);

  const syncPreviewAppData = useCallback((id: PreviewApp.UISaasValueType["id"]) => {}, []);

  const previewToSaasUiData = useCallback((id: PreviewApp.UISaasValueType["id"]) => {
    let target = find(previewAppListSignal.value, ["id", id]);
    //console.log("saasUiDataToPreview: ", target?.settings?.domain);
    if (target) {
      // FIXME: 当canvas 中的节点因为其他原因被删除，导致appList 中的节点对应真实节点已删除，这个时候需要校验数据完整性
      // 合并最新的 nodes 到 app 中
      target = PreviewAppUtil.mergeNodes(target, nodesRef.current);
      // bsf-4912 setDisplayThought
      setDisplayThought(target.output);
      setSaasUIData(target);
      setSelectedUI(target.value);
      // 每次选择app后需要设置inputIdArr
      const inputIdArr = (target.input || []).map(x => x.id);
      setInputId(inputIdArr);
      previewAppActiveIdSignal.value = id;
    }
  }, []);

  const saasUiDataToPreview = useCallback(() => {
    if (!previewAppActiveIdSignal.value) {
      return false;
    }

    const saasUIData = getSaasUIData();
    //console.log("saasUiDataToPreview: ", saasUIData.settings?.domain);
    // 如果不是默认值,则同步到 app list 中
    if (PreviewAppUtil.isDefaultPreviewApp(saasUIDataRef.current)) {
      return;
    }

    previewAppListSignal.value = previewAppListSignal.value.map(item => {
      if (previewAppActiveIdSignal.value === item.id) {
        const target = cloneDeep(item);
        const source = cloneDeep(saasUIData);
        // FIXME: 不知道为什么 app 列表中的input或output会有 重复的现象
        // mergeValue 此处是 sassuidata 和 applist 最后一处防止重复的屏障
        const newApp = PreviewAppUtil.mergeValue(target, source);
        // const newApp = {...target, ...source, v2App: {
        //   ...target.v2App,
        //   ...source.v2App,
        // }} as PreviewApp.UISaasValueType
        return newApp;
      }
      return item;

      // // 去掉 saas数据中的 function
      // // ... 扩展运算符会丢失第二级的字段
      // return previewAppActiveIdSignal.value === item.id
      //   ? PreviewAppUtil.merge(target, source)
      //   : item;
    });
  }, []);

  const getSaasUIData = useCallback(() => {
    // 拷贝一份获取一份新的saasuidata
    return PreviewAppUtil.filterSaasValueFunctions({ ...saasUIDataRef.current });
  }, []);

  const removePreviewById = useCallback((id: PreviewApp.UISaasValueType["id"]) => {
    // TODO:
  }, []);

  const handleSendToAppV2 = useCallback(
    (param: { cb: () => void; outputCb: () => void }) => {
      if (featureTags.enableSendToAppV2() === false) return false;

      // 如果在preview页面，不额外增加操作，直接添加
      if (showPreviewPanel && previewShowStatus) return false;

      // 暂存应当继续调用的方法
      sendToAppStagingData.value = param;
      // 打开preview
      setShowPreviewPanel(true);

      // 直接显示preview页面或显示app list页面时默认设置模板为saas
      if (previewAppList.length) {
        // 这里赋值是因为addIndividual方法涉及selectedui数据处理，默认需要选择第一个模
        setSelectedUI("saas");
      } else {
        if (disableSelectTemplate) {
          setSelectedUI("saas");
        }
      }
      return true;
    },
    [featureTags, previewShowStatus, previewAppList]
  );
  const enableRemovePreview = (hasAdded = true) => {
    // 有previewv2 featuretag且当前node在preview app 中
    if (hasAdded) {
      // 未打开preview 面板不做任务动作修改
      if (!showPreviewPanel) {
        return true;
      }
      // 列表页不作修改
      if (isInHistory) {
        return true;
      }

      return !!previewShowStatus;
    }
    return true;
  };

  const hasEnablePreviewAppByUIData = useCallback(() => {}, []);

  const clearPreviewApp = useCallback(() => {
    previewAppListSignal.value = [];
    previewAppActiveIdSignal.value = "";
    currentProjectSavedPreviewAppListSignal.value = [];
  }, []);

  const loadPreviewApp = (props: PreviewApp.LoadPreviewAppArgs) => {
    const { selectedExistingProject, UIData, selectedUIParam, isShare, setImageScrollDireaction, compatibleInput } =
      props;

    let checkedUIList = [];
    if (func.isEmpty(UIData)) return;
    let previewApp = {} as PreviewApp.UISaasValueType | undefined;
    if (isShare) {
      // 加载到老版本数据时,使用转换后到 appid
      const appId =
        (store.getState() as any).editor?.subscriberParamsData?.attributes?.appId ||
        selectedExistingProject?.v3?.previewAppId;
      previewApp = UIData.find((x: any) => x.id === appId);
    } else if (selectedExistingProject?.v3?.previewAppId) {
      previewApp = UIData.find((x: any) => x.id === selectedExistingProject?.v3?.previewAppId);
    }

    // 将project 上面的 UIData 赋值给 appList
    previewAppListSignal.value = UIData;
    previewAppActiveIdSignal.value = previewApp?.id || "";

    if (!func.isEmpty(previewApp) && previewApp) {
      const isHorizontal = previewApp.output.some((y: any) => y.angle === "Image List");
      const text = isHorizontal ? "horizontalScroll" : "verticallyScroll";
      setImageScrollDireaction(text);
      const count = HomeUsePluginUtil.getPreviewIndexByOutput(previewApp.output);

      UIConfigRef.current.saas.previewIndexRef.current = count;
      const input = compatibleInput(previewApp.input, isShare, previewApp?.staticMode || false);

      // FIXME: 去掉了非分享页刷新加载数据设置 saasuidata 的流程
      if (isShare) {
        UIConfigRef.current.saas.setUIDataFunc(
          PreviewAppUtil.genPreviewApp(previewApp, {
            title: previewApp.title || WEB_APP_DEFAULT_TITLE,
            // bsf-6211 subtitle 默认没有了
            subTitle: previewApp.subTitle || PreviewAppUtil.isChatAiApp(previewApp) ? "" : WEB_APP_DEFAULT_SUBTITLE,
            placeholder: previewApp.placeholder || "Enter something ...",
            staticMode: enableStaticMode || isShare ? previewApp?.staticMode || false : false,
            input,
            // v2app 字段在 loadPreviewApp 中应该必有
            v2App: previewApp.v2App as any,
          })
        );
      }

      // UIConfigRef.current.saas.setUIDataFunc({
      //   id: previewApp?.id,
      //   title: previewApp.title || WEB_APP_DEFAULT_TITLE,
      //   titleV3: previewApp?.titleV3 || {},
      //   subTitle: previewApp.subTitle || WEB_APP_DEFAULT_SUBTITLE,
      //   subTitleV3: previewApp?.subTitleV3 || {},
      //   placeholder: previewApp.placeholder || "Enter something ...",
      //   showTitle: previewApp.showTitle,
      //   showSubTitle: previewApp.showSubTitle,
      //   titleStyle: previewApp.titleStyle,
      //   subTitleStyle: previewApp.subTitleStyle,
      //   theme: previewApp?.theme,
      //   fontSize: previewApp?.fontSize,
      //   previewV2: previewApp?.previewV2,
      //   staticMode: enableStaticMode || isShare ? previewApp?.staticMode || false : false,
      //   input,
      //   output: [...previewApp.output],
      //   v2App: previewApp.v2App,
      //   value: previewApp.value,
      // });

      checkedUIList = [
        {
          label: previewApp.label,
          value: previewApp.value,
          checked: true,
        },
      ];

      if (enable_saas_web === false && some(checkedUIList, checkedUI => checkedUI.value === "saasV2")) {
        // Do nothing
      } else {
        setShowPreviewPanel(true);

        if (previewApp?.id) {
          previewToSaasUiData(previewApp?.id);
          setPreviewShowStatus(true);
          setHistoryShowStatus(true);
        }
      }

      setCheckedUIList(checkedUIList);
      // 设置一个初始选中的ui
      const currDisplayUI = selectedUIParam || checkedUIList[0].value;
      setSelectedUI(currDisplayUI);
      // 初始化preview input, 这里直接使用接口返回的input数据
      const inputIdArr = input.map((x: any) => x.id);
      setInputId(inputIdArr);
    }

    // FIXME: 重新赋值一次 selectTemplate
    setSelectedTemplate(selectedExistingProject);
  };
  const clearCurrentPreviewApp = useCallback(() => {
    previewAppActiveIdSignal.value = "";
    setSaasUIData(PreviewAppUtil.genDefaultUISaasValue());
    // bsf-4912 setDisplayThought
    setDisplayThought([]);
  }, []);

  /**
   * 获取当前项目所有数据，包括 ui, v3, publish
   */
  const getSelectedTemplate = useCallback((clone: boolean = false) => {
    const selectedTemplate = (store.getState() as any).studio.selectedTemplate as PreviewApp.ProjectType | undefined;
    if (selectedTemplate) {
      // FIXME: 当input 中有 function 类型时应该去掉,否则设置为redux时会报错?
      return clone ? { ...selectedTemplate } : selectedTemplate;
    }
    return;
  }, []);

  const getPublishMetadata = useCallback(
    (id?: PreviewApp.UISaasValueType["id"]): PreviewApp.PublishedMetadataV2Type | undefined => {
      const project = getSelectedTemplate();
      // v2 preview

      if (id) {
        return PreviewAppUtil.getTargetPublishMetaById(project, id);
      }

      return PreviewAppUtil.getTargetPublishMetaById(project, previewAppActiveIdSignal.value);
    },
    [previewAppActiveIdSignal.value]
  );
  const updateSelectTemplate = (updateObj: any) => {
    const selectedTemplate = getSelectedTemplate();
    const updatedSelectedTemplate = Object.assign({}, selectedTemplate, {
      ...updateObj,
    });
    setSelectedTemplate(updatedSelectedTemplate);
  };
  const updatePublishedMetadataV2 = (updateObj: Partial<PreviewApp.PublishedMetadataV2Type>): void => {
    const obj = {
      id: previewAppActiveIdSignal.value,
      ...updateObj,
    };
    const project: any = { ...getSelectedTemplate() };
    let published_metadata_v2 = project?.published_metadata_v2 ? [...project?.published_metadata_v2] : [];

    if (isBlank(published_metadata_v2)) {
      published_metadata_v2 = [obj];
    } else {
      const idx = published_metadata_v2.findIndex(
        (x: PreviewApp.PublishedMetadataV2Type) => x.id === previewAppActiveIdSignal.value
      );
      if (idx !== -1) {
        const newData = Object.assign({}, published_metadata_v2[idx], obj);
        published_metadata_v2[idx] = newData;
      } else {
        published_metadata_v2.push(obj);
      }
    }
    project.published_metadata_v2 = published_metadata_v2;
    setSelectedTemplate(project);
  };
  const delPublishedMetadataV2 = (id: string): PreviewApp.PublishedMetadataV2Type[] => {
    const project: any = { ...getSelectedTemplate() };
    const publishedMetadataV2 = project?.published_metadata_v2 ? [...project?.published_metadata_v2] : [];
    const idx = publishedMetadataV2.findIndex((x: PreviewApp.PublishedMetadataV2Type) => x.id === id);

    if (idx !== -1) {
      publishedMetadataV2.splice(idx, 1);
    }
    return publishedMetadataV2;
  };
  const previewAppActivePublishMetadataV2Item = () => {
    const project: any = { ...getSelectedTemplate() };
    let published_metadata_v2 = project?.published_metadata_v2 ? [...project?.published_metadata_v2] : [];
    return published_metadata_v2.find(
      (x: PreviewApp.PublishedMetadataV2Type) => x.id === previewAppActiveIdSignal.value
    );
  };

  const comeInSetting = (id: string) => {
    const target = find(previewAppListSignal.value, ["id", id]);
    if (target) {
      previewToSaasUiData(id);
      setPreviewShowStatus(true);
      setHistoryShowStatus(true);
      previewPaneStore.settingPageStatus(true);
      updatePublishedMetadataV2({ charge_option: "Subscription" });
      // 匹配appId=
      let newUrl = window.location.href.replace(/(\?|&?)(appId=([^&]*&?))$|(appId=([^&]*&?))/g, "");
      // 匹配type=stripeConnectExpired
      newUrl = newUrl.replace(/(\?|&?)(type=stripeConnectExpired&?)$|(type=stripeConnectExpired&?)/g, "");
      window.history.pushState({}, "", newUrl);
    } else {
      func.messageUtil("App ID does not exist");
    }
  };

  const getPreviewAppActiveLable = useCallback(() => {
    const activeItem = previewAppList.find((x: PreviewApp.UISaasValueType) => x.id === previewAppAcivieId);
    return activeItem?.v2App?.type;
  }, [previewAppAcivieId, previewAppList]);

  const removeByNodeIds = useCallback((ids: string[]) => {
    previewAppListSignal.value = PreviewAppUtil.removeByNodeIds(previewAppListSignal.value, ids);
  }, []);

  function edgeSourceNodeAsInput(nodeids: string[]) {
    const edgeNodes = nodeids?.filter(id => NEW_EDGE_REG.test(id));

    if (!isBlank(edgeNodes)) {
      const allSourceNodes = getNodes()
        .flatMap(node => {
          if (node.data.flows && edgeNodes.includes(node.id)) {
            const flows = node.data.flows as { sourceNodeId: string }[];
            return flows.map(flow => flow.sourceNodeId);
          }
          return "";
        })
        ?.filter(id => !isBlank(id));
      return allSourceNodes;
    }
    return [];
  }

  const triggerRemovePreviewAppNode = useCallback((args: PreviewApp.RemovePreviewAppNodeArgs) => {
    const { ids, edgeNodeIds, onSuccess } = args;

    let removeIds: string[] = [];

    let modalDes = "";
    const edgeSource = edgeSourceNodeAsInput(edgeNodeIds);
    if (PreviewAppUtil.hasNodeIds(previewAppListSignal.value, edgeSource)) {
      // 暂时由开发确定为如下文字 后续有变化再改
      modalDes = "This edge sourceNode is currently being used by preview app";
      removeIds = edgeSource;
    }

    if (PreviewAppUtil.hasNodeIds(previewAppListSignal.value, ids)) {
      removeIds = [...edgeSource, ...ids];
      modalDes = "This node is currently being used by preview app";
    }

    if (modalDes) {
      showModal({
        title: "Warning",
        width: 520,
        subTitle: modalDes,
        buttons: ["Cancel", "Confirm"],
        onOk: ({ close }) => {
          onSuccess(removeIds);
          removeByNodeIds(removeIds);
          close();
        },
      });
    } else {
      onSuccess(removeIds);
    }
  }, []);

  const clearPublishMetadata = useCallback(() => {
    const selectTemp = getSelectedTemplate(true);
    if (!selectTemp) {
      return;
    }
    const mateData = selectTemp.published_metadata_v2?.filter(item => {
      return item.id !== previewAppActiveIdSignal.value;
    });
    selectTemp.published_metadata_v2 = mateData;
    return selectTemp;
  }, []);

  const assignPreviewAppData = useCallback((target: any, extra?: PreviewApp.ExtraArgsType) => {
    saasUiDataToPreview();
    // 扩展 publish metadata
    return PreviewAppUtil.assignPreviewAppData(target, {
      appId: previewAppActiveIdSignal.value,
      appList: previewAppListSignal.value,
      isNewEdge: isNewEdgeRunRef.current,
      nodes: nodesRef.current,
      lastIndex: vDataLastIndex.value,
      ...extra,
    });
  }, []);

  const setShowPreviewPanel = useCallback(
    (value: boolean) => {
      _setShowPreviewPanel(value);
      if (!value && publishShowStatus) {
        // FIXME: 在某种情况下会打开previewpanl 后activeid 被清空
        // 如果 关闭面板 同步 saasuidata 同 preview
        saasUiDataToPreview();
      }
    },
    [_setShowPreviewPanel, saasUiDataToPreview]
  );

  const updatePreviewV2App = useCallback((id: any, value: Partial<Omit<PreviewApp.PreviewAppV2FieldType, "id">>) => {
    previewAppListSignal.value = previewAppListSignal.value.map(item => {
      if (item.id === id) {
        return {
          ...item,
          v2App: {
            // 为了类型安全
            id: item.id,
            ...item.v2App,
            ...value,
          },
        };
      }
      return item;
    });
  }, []);

  const deletePreviewV2App = useCallback((id: any) => {
    previewAppListSignal.value = previewAppListSignal.value.filter(item => item.id !== id);

    const selectedTemplate = getSelectedTemplate();
    const updatedUISelectedTemplate = assignPreviewAppData(selectedTemplate);

    updateSelectTemplate({
      ...updatedUISelectedTemplate,
      published_metadata_v2: delPublishedMetadataV2(id),
    });
  }, []);

  const updatedv2AppData = useCallback(() => {
    let updatedProject: any = getSelectedTemplate(true);
    if (isNewEdgeRunRef.current) {
      updatedProject = {
        ...updatedProject,
        v3: { ...updatedProject.v3, UI: previewAppListSignal.value },
      };
    } else {
      updatedProject = { ...updatedProject, v2: { ...updatedProject.v2, UI: previewAppListSignal.value } };
    }
    return updatedProject;
  }, []);

  // 选择app
  const onSelectPreviewApp = useCallback(
    (option: PreviewApp.UISaasValueType) => {
      // TODO 设置被选中的数据
      previewToSaasUiData(option.id);
      setPreviewShowStatus(true);
    },
    [previewToSaasUiData, setPreviewShowStatus, setHistoryShowStatus]
  );

  const disabledPublish = useMemo(() => {
    // bsf-4912 replace thoughts
    return PreviewAppUtil.isDisabledPublish({
      saasUIData: saasUIData,
      isStaticMode: !!isStaticApp,
      thoughts: saasUIData.output,
      selectedUI: selectedUI,
      nodes: nodesRef.current,
    });
  }, [saasUIData, isStaticApp, selectedUI]);

  return {
    /**
     * 用于验证当前打开的(或新建的)project是否有未保存的更改
     */
    checkIfCurrentProjectPreviewListIsChanged,
    updateCurrentProjectSavedPreviewList,
    clearCurrentProjectSavedPreviewList,

    previewAppListSignal,
    previewAppList,
    previewAppActiveIdSignal,
    previewAppAcivieId,
    /**
     * 是否开启 previewAPP 功能
     */
    showDeleteTipSignal,
    /**
     * 是否禁用 publish
     */
    disabledPublish,
    appStates,

    // publish flow v2 版本为 previewPanel 提供的四个panel 显示状态
    isInHistory,
    showPreviewPanel,
    showPreviewPanelRef,
    setShowPreviewPanel,
    historyShowStatus,
    setHistoryShowStatus,
    publishShowStatus,
    setPublishShowStatus,
    previewShowStatus,
    setPreviewShowStatus,
    settingShowStatus,
    setSettingShowStatus,
    openPreviewPage,

    /**
     * 回到列表
     */
    returnToPreviewList,
    replacePreviewAppList,
    addPreview,
    removePreviewById,

    previewToSaasUiData,
    saasUiDataToPreview,
    handleSendToAppV2,
    enableRemovePreview,

    /**
     * 用于进入页面加载 ui 数据
     */
    loadPreviewApp,
    /**
     * 检查数据中是否有新 previewAPP 功能
     */
    hasEnablePreviewAppByUIData,

    clearPreviewApp,
    clearCurrentPreviewApp,

    onSelectPreviewApp,
    /**
     * - 如果没有开启了v2 preview 权限,直接返回当前项目的 published_metadata
     * - 如果传入 id 且开启 v2 preview 权限，获取对应ID的 published_metadata
     * - 如果没有ID，且开启了v2 preview 默认使用当前activeID的 published_metadata
     */
    getPublishMetadata,
    getPreviewAppActiveLable,
    removeByNodeIds,

    clearPublishMetadata,
    /**
     * 扩展 appId 或 publish_metadata
     *
     * - 如果没有开启 v2 preview 权限则原样返回
     * - 如果传入 appID 则使用传入id,没有传入id 则使用当前状态的id
     * - 如果传入 publish_metadta 则使用,否则原样返回
     */
    assignPreviewAppData,
    getSelectedTemplate,
    /**
     * 更新置顶preview 中的v2app 字段
     */
    updatePreviewV2App,

    // 删除其中一个数据
    deletePreviewV2App,
    updatedv2AppData,
    /**
     * preview app 删除节点触发方法
     * - 如果开启 preview v2 会弹窗拦截
     * - 如果没有开启 previwe v2 直接调用 onSuccess
     */
    triggerRemovePreviewAppNode,
    chargeOptionUpdatedSignal,
    updatePublishedMetadataV2,
    previewAppActivePublishMetadataV2Item,
    comeInSetting,
  };
}

export const { Provider: PreviewAppProvider, useContainer: usePreviewAppStore } = createContainer(usePreviewApp);
